/**
 * Turning the free-text import columns into kilograms — mirrors App\Support\ProductionKg
 * on the backend.
 *
 * `waga` is free text: it may be empty, use a comma as the decimal separator, carry a unit
 * suffix ("0,5 kg") or contain junk from misaligned import files (colour names, Excel date
 * serials such as 46083). Anything above the plausibility cap contributes 0 kg.
 */

/** Heaviest real spool in the data is 20 kg — see the `waga_prog` setting. */
export const MAX_PLAUSIBLE_WEIGHT_KG = 1000;

/** Per-piece weight in kg; 0 when the value is empty or implausible. */
export const parseWeightKg = (waga: string | number | null | undefined): number => {
  if (waga === null || waga === undefined) return 0;

  const match = String(waga).replace(',', '.').match(/[0-9]+(?:\.[0-9]+)?/);
  if (!match) return 0;

  const weight = parseFloat(match[0]);
  if (!isFinite(weight) || weight <= 0 || weight > MAX_PLAUSIBLE_WEIGHT_KG) return 0;

  return weight;
};

/** Integer quantity from a free-text column; 0 when it does not parse. */
export const parseQty = (value: string | number | null | undefined): number => {
  const parsed = parseInt(String(value ?? ''), 10);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Whole pieces in a weighed kilogram total (the remainder is not a piece yet) — mirrors
 * ProductionKg::kgToPieces, in grams so 13,5 / 4,5 is exactly 3.
 */
export const kgToPieces = (kg: number, unitKg: number): number => {
  const unit = Math.round(unitKg * 1000);
  return unit > 0 ? Math.floor(Math.round(kg * 1000) / unit) : 0;
};

/** Formats a kilogram quantity with up to three decimals (no trailing zeros). */
export const formatKgShort = (kg: number): string =>
  kg.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

/** Formats kilograms the Polish way, always with two decimals. */
export const formatKg = (kg: number): string =>
  kg.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface SectionRowFields {
  id: number;
  do_produkcji?: string | number | null;
  waga?: string | number | null;
  indeks_kat?: string | null;
}

/**
 * Section header rows come from the Excel import: rows whose production fields are empty.
 * They render as the grey bar that groups the items below them (typically one dye).
 */
export const isSectionHeaderRow = (item: SectionRowFields): boolean => {
  const rawDoProd = String(item.do_produkcji ?? '').trim();
  const rawWaga = String(item.waga ?? '').trim();
  const rawIndeks = String(item.indeks_kat ?? '').trim();
  return (rawDoProd === '' || rawDoProd === '0') &&
         (rawWaga === '' || rawWaga === '0') &&
         rawIndeks === '';
};

/**
 * Total weight (kg) per section, keyed by the header row id: the sum of
 * TO PRODUCE x per-piece weight over the items between that header and the next one.
 */
export const sectionTotalWeightsKg = <T extends SectionRowFields>(items: T[]): Map<number, number> => {
  const totals = new Map<number, number>();
  let currentHeaderId: number | null = null;

  items.forEach((item) => {
    if (isSectionHeaderRow(item)) {
      currentHeaderId = item.id;
      totals.set(item.id, 0);
      return;
    }
    if (currentHeaderId === null) return;
    const kg = parseQty(item.do_produkcji) * parseWeightKg(item.waga);
    totals.set(currentHeaderId, (totals.get(currentHeaderId) ?? 0) + kg);
  });

  return totals;
};
